import { addMinutes, format, getTime } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useForm } from 'react-hook-form';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import DatePicker from '@components/form/DatePicker/DatePicker';
import TextArea from '@components/form/TextArea/TextArea';
import ModalHeader from '@components/modals/ModalHeader/ModalHeader';
import Button from '@components/uikit/Button/Button';

import { filterPassedTimeForSnooze } from '@helpers/filterPassedTimeForSnooze';
import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { SNOOZE_ACTION_ITEM_FIELDS, schemaSnoozeActionItem } from './constants/schemaSnoozeActionItem';
import { useEditSnoozeMutation } from './services/useEditSnoozeMutation';
import { useGetSnoozeReasonQuery } from './services/useGetSnoozeReasonQuery';
import style from './style.module.scss';

interface IProps {
  actionItemId: number;
  facilityTimezone: number;
  remindDate: string;
}

const EditSnoozeModal: React.FC<IProps> = ({ actionItemId, facilityTimezone, remindDate }) => {
  const { t } = useTranslation();
  const { setModalClose } = useModalActions();
  const { data: profileData } = useUserProfileQuery();

  const { data: getSnoozeReasonData, isFetching: getSnoozeReasonIsFetching } = useGetSnoozeReasonQuery({
    actionItemId,
  });

  const { mutate: editSnoozeMutate, isLoading: editSnoozeIsLoading } = useEditSnoozeMutation(actionItemId);

  const dateTimeFormat = `${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`;

  const utcTime = formatInTimeZone(new Date(), 'UTC', 'yyyy/MM/dd HH:mm');

  const currentFacilityDateTime = addMinutes(utcTime, facilityTimezone * 60);

  const currentFacilityRemindDateTime = addMinutes(new Date(remindDate), facilityTimezone * 60);

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm({
    mode: 'all',
    resolver: schemaSnoozeActionItem(currentFacilityDateTime),
    values: {
      date: currentFacilityRemindDateTime,
      description: getSnoozeReasonData?.reason || '',
    },
  });

  const submit = handleSubmit((formData) => {
    editSnoozeMutate(
      {
        actionItemId,
        description: formData.description,
        end_snooze_date: format(addMinutes(formData.date, -facilityTimezone * 60), 'yyyy/MM/dd HH:mm'),
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('The action item has been snoozed.') });

          setModalClose();
        },
      },
    );
  });

  return (
    <div className={style.modalSnooze}>
      <ModalHeader />
      <div className={style.modalBody}>
        <div className={style.modalBodyTitle}>{t('Edit Snooze')}</div>
        {getSnoozeReasonIsFetching ? (
          <div className={style.skeletonDatePickerWrapper}>
            <SkeletonBlock />
          </div>
        ) : (
          <DatePicker
            control={control}
            name={SNOOZE_ACTION_ITEM_FIELDS.DATE}
            dateFormat={dateTimeFormat}
            minDate={currentFacilityDateTime}
            filterTime={(time) => filterPassedTimeForSnooze(time, currentFacilityDateTime)}
            showMonthDropdown
            showYearDropdown
            showTimeSelect
            label={t('Remind Me On This Date')}
            required
            isFacilityOffsetAdded
            timeCaption={format(getTime(currentFacilityDateTime), 'HH:mm')}
          />
        )}

        <div className={style.textArea}>
          {getSnoozeReasonIsFetching ? (
            <div className={style.skeletonTextAreaWrapper}>
              <SkeletonBlock />
            </div>
          ) : (
            <TextArea
              control={control}
              name={SNOOZE_ACTION_ITEM_FIELDS.DESCRIPTION}
              label={t('Reason for snooze')}
              required
            />
          )}
        </div>
      </div>
      <div className={style.modalFooter}>
        <Button
          type="primary"
          text={t('Confirm')}
          disabled={editSnoozeIsLoading || !isDirty}
          onClick={submit}
        />
        <Button
          type="secondary"
          text={t('Cancel')}
          disabled={editSnoozeIsLoading}
          onClick={() => setModalClose()}
        />
      </div>
    </div>
  );
};

export default EditSnoozeModal;
